






































































import {
  computed, defineComponent, onMounted, onUnmounted, ref, useRoute, watch,
} from '@nuxtjs/composition-api';
import { SfCarousel, SfLoader, SfSection } from '@storefront-ui/vue';
import SvgImage from '~/components/General/SvgImage.vue';
import HorizontalCard from '~/components/JetUi/organisms/Product/HorizontalCard.vue';
import SfProductCard from '~/components/JetUi/organisms/Product/SfProductCard.vue';
import SkeletonLoader from '~/components/SkeletonLoader/index.vue';
import { useProduct } from '~/composables';
import { useAddToCart } from '~/helpers/cart/addToCart';
import { ProductStockStatus, SortEnum } from '~/modules/GraphQL/types';
import productGetters from '~/modules/catalog/product/getters/productGetters';
import type { Product } from '~/modules/catalog/product/types';
import useWishlist from '~/modules/wishlist/composables/useWishlist';

export default defineComponent({
  name: 'CategoriesPage',
  components: {
    SvgImage,
    SfSection,
    SfLoader,
    SfCarousel,
    SfProductCard,
    SkeletonLoader,
    HorizontalCard,
  },
  props: {
    searchQuery: {
      type: String,
      default: '',
    },
    isCarousel: {
      type: Boolean,
      default: false,
    },
    sort: {
      type: String,
      default: 'relevance',
    },
    sortDirection: {
      type: String,
      default: 'DSC',
    },
  },
  emits: ['click:wishlist'],
  setup(props) {
    const route = useRoute();
    const filter = ref(['']);
    const qty = ref(1);
    const title = ref('Espacial Suprimentos');
    const selectedSort = ref(props.sort);
    const { getProductList, loading, getProductPath } = useProduct();
    const { isInWishlist, addOrRemoveItem } = useWishlist();
    const { addItemToCart, isInCart } = useAddToCart();
    const products = ref([]);
    const totalProducts = ref<number>(0);
    const categoryId = ref();
    const showProducts = ref(false);
    const selectedSortDirection = ref(props.sortDirection);
    const showSkeleton = ref(true);
    const grid = ref<any>(null);
    const page = ref(1);
    const perView = ref(1);
    const isClient = process.client;
    const windowWidth = ref(isClient ? window.innerWidth : 1200);

    watch(
      () => props.sortDirection,
      (newDirection) => {
        selectedSortDirection.value = newDirection;
      },
    );

    const catProductClass = (product: Product) => {
      const categoryClass = product?.categories?.[0]?.name || '';
      return `${categoryClass}`;
    };

    const addItemToWishlist = async (product: Product) => {
      try {
        await addOrRemoveItem({ product });
      } catch (error) {
        console.error('Error adding item to wishlist:', error);
      }
    };

    const handleResize = () => {
      windowWidth.value = window.innerWidth;
      calculateItemsPerRow();
    };

    const handleScroll = async () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if ((scrollTop + clientHeight >= scrollHeight - 2000) && !loading.value && showProducts.value) {
        await fetchProducts();
      }
    };

    onMounted(async () => {
      if (isClient) {
        filter.value = (route.value.query?.list as string ?? '').split(' ');
        title.value = route.value.query?.title as string;
        categoryId.value = ((route.value.query?.categoryId as string) ?? '').split(',');
        handleResize();
        await fetchProducts();
        showProducts.value = true;

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll);
      }
    });

    onUnmounted(() => {
      if (isClient) {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll);
      }
    });

    function isSku(arr: string[]): boolean {
      return arr.some((item) => !isNaN(Number(item)) && item.trim() !== '');
    }

    function shuffleArray(array: Array<any>) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    }

    const getFilters = (filters: string[]) => {
      if (isSku(filters)) {
        return ['', { filter: { sku: { in: filters } } }];
      } 
       if (!categoryId.value[0]) {
        const searchTerm = filters.join(' ');
        return [searchTerm, { category: searchTerm }];
      }
      return ['', { categoryId: categoryId.value }];
    };

    const calculateItemsPerRow = () => {
      if (!grid.value) return;
      const children = [...grid.value.children];

      if (children.length === 0) {
        perView.value = 0;
        return;
      }

      const firstRowOffsetTop = (children[0] as any).offsetTop;
      perView.value = children.filter(
        (child: any) => child.offsetTop === firstRowOffsetTop,
      ).length;
    };

    const nextProducts = computed(() => {
      const productsLength = products.value.length;
      if (productsLength % perView.value === 0) {
        return perView.value;
      }
      return 2 * perView.value - (productsLength % perView.value);
    });

    const fetchProducts = async () => {
      try {
        const sortType = selectedSort.value;
        const filters = getFilters(filter.value);
        const arrayFilter = filters[1] as Object;
        const sortDirection = selectedSortDirection.value === 'ASC' ? SortEnum.Asc : SortEnum.Desc;
        const productList = await getProductList({
          search: filters[0] as string,
          pageSize: 50,
          currentPage: page.value,
          sort: { [sortType]: sortDirection },
          ...arrayFilter,
        });

        if (productList?.items?.length) {
          const productsWithTypename = productList.items.map((product) => ({
            ...product,
            typename: 'SimpleProduct',
          }));

          products.value.push(...shuffleArray(productsWithTypename));
          totalProducts.value = productList.total_count;
          page.value++;
        } else {
          showSkeleton.value = false;
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    const isInStock = (product) => product.stock_status === ProductStockStatus.InStock;

    return {
      fetchProducts,
      totalProducts,
      selectedSort,
      products,
      title,
      productGetters,
      imageSizes: { productCard: { width: 200, height: 200 } },
      isInWishlist,
      isInCart,
      addItemToWishlist,
      addItemToCart,
      getProductPath,
      catProductClass,
      handleQtyUpdate: (quantity: number) => {
        qty.value = quantity;
      },
      loading,
      selectedSortDirection,
      qty,
      showProducts,
      nextProducts,
      grid,
      showSkeleton,
      isInStock,
      windowWidth,
    };
  },
  head() {
    return {
      title: this.$t(this.title as string).toString(),
    };
  },
});
